/** How soon a change to the same field must follow the last one to join its step. */
export const MERGE_MS = 1000;
const MAX_STEPS = 200;

/**
 * Undo and redo over whole states, so any change to them is undoable without its own
 * wiring. The owner records the state before each change and hands over the current one to
 * step away from it.
 */
export class History<T> {
  private past: T[] = [];
  private future: T[] = [];
  private last: { readonly field: string; readonly at: number } | null = null;

  constructor(
    private readonly now: () => number = Date.now,
    private readonly limit = MAX_STEPS,
  ) {}

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  /** Records the state before a change; quick changes to one `field`, such as typing, are one step. */
  record(before: T, field?: string): void {
    this.future = [];
    const at = this.now();
    const joins = field !== undefined && this.last?.field === field && at - this.last.at < MERGE_MS;
    this.last = field === undefined ? null : { field, at };
    if (joins) return;
    this.past.push(before);
    if (this.past.length > this.limit) this.past.shift();
  }

  undo(current: T): T | null {
    return this.step(this.past, this.future, current);
  }

  redo(current: T): T | null {
    return this.step(this.future, this.past, current);
  }

  clear(): void {
    this.past = [];
    this.future = [];
    this.last = null;
  }

  private step(from: T[], to: T[], current: T): T | null {
    const state = from.pop();
    if (state === undefined) return null;
    to.push(current);
    this.last = null;
    return state;
  }
}
